import { CalendarDays, DollarSign } from 'lucide-react';
import { type ComponentProps, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type CommitInputProps = Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'defaultValue'> & {
  /** The saved value, formatted. Shown again when an edit is cancelled or rejected. */
  display: string;
  /**
   * Called on blur/Enter when the text changed. Return the text to show (the edit was accepted),
   * or false to reject it and restore `display`.
   */
  onCommit: (text: string) => string | false;
  /** Bump to restore `display` (e.g. when an accepted edit later failed to save). */
  revision?: number;
  /** Show the full text in a tooltip on hover (for a truncated field); never while editing. */
  fullTextOnHover?: boolean;
};

/**
 * Clockify-style inline input: edits commit on blur or Enter, Esc restores the saved value.
 */
export function CommitInput({
  display,
  onCommit,
  onKeyDown,
  onBlur,
  onFocus,
  revision,
  fullTextOnHover,
  ...props
}: CommitInputProps) {
  const [text, setText] = useState(display);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const cancelled = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `revision` forces a reset after a failed save
  useEffect(() => setText(display), [display, revision]);

  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      setText(display);
      return;
    }
    if (text === display) return;
    const shown = onCommit(text);
    setText(shown === false ? display : shown);
  };

  const keyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelled.current = true;
      setText(display);
      e.currentTarget.blur();
    }
  };

  const input = (
    <Input
      {...props}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={keyDown}
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        commit();
        onBlur?.(e);
      }}
    />
  );
  if (!fullTextOnHover) return input;
  return (
    <Tooltip open={hovered && !focused && text !== ''} onOpenChange={setHovered}>
      <TooltipTrigger asChild>{input}</TooltipTrigger>
      <TooltipContent className="max-w-md">{text}</TooltipContent>
    </Tooltip>
  );
}

/** Clockify's `$` toggle. */
export function BillableToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label="Billable"
      aria-pressed={value}
      title={value ? 'Billable' : 'Non-billable'}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={cn(value ? 'text-primary' : 'text-muted-foreground/60')}
    >
      <DollarSign className="size-4" />
    </Button>
  );
}

const toLocalDate = (date: string) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
};
const fromLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Calendar popover for picking a YYYY-MM-DD date. Shows the icon, plus `label` when given. */
export function DatePicker({
  value,
  onChange,
  label,
  ariaLabel = 'Date',
  weekStart = 1,
  className,
}: {
  value: string;
  onChange: (date: string) => void;
  label?: string;
  ariaLabel?: string;
  weekStart?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={label ? 'sm' : 'icon-sm'}
          aria-label={ariaLabel}
          title={value}
          className={cn('text-muted-foreground', className)}
        >
          <CalendarDays className="size-4" />
          {label && <span>{label}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={toLocalDate(value)}
          defaultMonth={toLocalDate(value)}
          weekStartsOn={weekStart as 0 | 1 | 2 | 3 | 4 | 5 | 6}
          onSelect={(d) => {
            if (!d) return;
            onChange(fromLocalDate(d));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** The server's message for a failed request (time errors such as 409s are written for people). */
export const errorMessage = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');
